import React from 'react';
import Flag from '../common/Flag.jsx';
import { normalizeCardPlayer, tierOf } from './cardUtils.js';
import useFitText from './useFitText.js';

const OUTFIELD_STATS = [
  [
    ['cro', 'CRO'],
    ['dri', 'DRI'],
    ['fin', 'FIN'],
    ['cre', 'CRE'],
  ],
  [
    ['pas', 'PAS'],
    ['aer', 'AER'],
    ['def', 'DEF'],
    ['win', 'WIN'],
  ],
];

const GOALKEEPER_STATS = [
  ['sho', 'SHO'],
  ['pas', 'PAS'],
  ['agg', 'AGG'],
];

const CARD_SIZES = new Set(['sm', 'md', 'lg']);

function StatRow({ label, value }) {
  return (
    <div className="pcard__stat-row">
      <span className="pcard__stat-label">{label}</span>
      <strong className="pcard__stat-value">{value ?? '-'}</strong>
    </div>
  );
}

function NovaStar() {
  return (
    <svg className="pcard__star-svg" viewBox="0 0 48 48" aria-hidden="true">
      <circle className="pcard__star-glow" cx="24" cy="24" r="14" />
      <path d="M24 3.5 28.1 19.9 44.5 24 28.1 28.1 24 44.5 19.9 28.1 3.5 24 19.9 19.9Z" />
      <circle className="pcard__star-core" cx="24" cy="24" r="3" />
    </svg>
  );
}

export default function PlayerCard({ player, size = 'md', className = '' }) {
  const cardPlayer = normalizeCardPlayer(player);
  const tier = tierOf(cardPlayer.overall);
  const visualTier = tier || 'bronze';
  const resolvedSize = CARD_SIZES.has(size) ? size : 'md';
  const classes = [
    'pcard',
    `pcard--${visualTier}`,
    `pcard--${resolvedSize}`,
    !tier && 'pcard--unrated',
    className,
  ].filter(Boolean).join(' ');
  const nameRef = useFitText(cardPlayer.name);

  return (
    <article className={classes} aria-label={`${cardPlayer.name || 'Unrated'} player card`}>
      <div className="pcard__face">
        <div className="pcard__top">
          <div className="pcard__overall">
            <strong className="pcard__overall-value">{cardPlayer.overall ?? '-'}</strong>
            <span className="pcard__overall-label">OVERALL</span>
          </div>

          <div className="pcard__identity">
            <span className="pcard__flag">
              <Flag
                code={cardPlayer.nationalityCode}
                fallback={cardPlayer.nationalityName || ''}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            </span>
            <span className="pcard__position">{cardPlayer.positionGroup || '-'}</span>
          </div>
        </div>

        <div className="pcard__star">
          <NovaStar />
        </div>

        <div className="pcard__name" title={cardPlayer.name || undefined}>
          <span ref={nameRef} className="pcard__name-text">{cardPlayer.name || '-'}</span>
        </div>

        {cardPlayer.isGoalkeeper ? (
          <div className="pcard__stats pcard__stats--goalkeeper">
            <div className="pcard__stats-column">
              {GOALKEEPER_STATS.map(([key, label]) => (
                <StatRow key={key} label={label} value={cardPlayer.stats[key]} />
              ))}
            </div>
          </div>
        ) : (
          <div className="pcard__stats pcard__stats--outfield">
            {OUTFIELD_STATS.map((column, columnIndex) => (
              <div className="pcard__stats-column" key={columnIndex}>
                {column.map(([key, label]) => (
                  <StatRow key={key} label={label} value={cardPlayer.stats[key]} />
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
    </article>
  );
}
